import { useCallback, useState, useMemo } from 'react'
// @mui
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import useUsers from 'src/service/users.service'
import RHFAutocomplete, { OptionType } from 'src/components/hook-form/rhf-autocomplete'
import Iconify from 'src/components/iconify'
import { useBoolean } from 'src/hooks/use-boolean'
import { LoadingButton } from '@mui/lab'
import { Autocomplete } from '@mui/material'
import useDebounce from 'src/hooks/use-debounce'
import UserCreateEditForm from '../../users/user-create-edit-view'

// ----------------------------------------------------------------------

interface EventStepperTwoProps {
  methods: any
  activeStep: number
  handleBack: () => void
  handleNext: (data: any) => void
  isLoading?: boolean
  handleSave: () => Promise<void>
}

export default function EventStepperTwo({
  activeStep,
  handleBack,
  handleNext,
  methods,
  isLoading,
  handleSave,
}: EventStepperTwoProps) {
  const { getValues, watch } = methods
  const [search, setSearch] = useState('')
  const [inputValue, setInputValue] = useState('')
  const searchDebounce = useDebounce(search, 300)
  const quickEdit = useBoolean()

  const selectedManagers = watch('managers') || []

  const {
    isLoading: isLoadingUser,
    isFetching,
    data,
  } = useUsers(
    {
      page: 1,
      perPage: 50,
      sort: 'name',
    },
    {
      search: searchDebounce,
    },
  )

  const handleNextStep = useCallback(() => {
    handleNext(getValues())
  }, [getValues, handleNext])

  const options = useMemo(
    () =>
      data?.data.map((option) => ({
        label: option.name,
        value: option._id,
        email: option.email,
      })) || [],
    [data?.data],
  )

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && search.trim()) {
      event.preventDefault()
      // Could add quick user creation here if needed
    }
  }

  return (
    <>
      <Stack gap={3}>
        <Box>
          <Typography variant='h6' gutterBottom>
            Vendedores do Evento
          </Typography>
          <Typography variant='body2' color='text.secondary' gutterBottom>
            Selecione os vendedores que terão acesso a este evento. Se nenhum vendedor for
            selecionado, você será automaticamente adicionado.
          </Typography>
        </Box>

        {/* Quick Manager Selection */}
        <RHFAutocomplete
          options={options}
          loading={isLoadingUser || isFetching}
          multiple
          name='managers'
          label='Buscar e selecionar vendedores'
          placeholder='Digite o nome do vendedor'
          onInputChange={(_, newInputValue: string) => {
            setSearch(newInputValue)
          }}
          getOptionLabel={(option: OptionType | string) => (option as OptionType).label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              <Stack>
                <Typography variant='body2'>{option.label}</Typography>
                {option.email && (
                  <Typography variant='caption' color='text.secondary'>
                    {option.email}
                  </Typography>
                )}
              </Stack>
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.value}
                label={option.label}
                size='small'
                color='primary'
                variant='outlined'
              />
            ))
          }
        />

        {/* Selected Managers Display */}
        {selectedManagers.length > 0 && (
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant='subtitle2' gutterBottom>
              Vendedores Selecionados ({selectedManagers.length})
            </Typography>
            <Stack direction='row' flexWrap='wrap' gap={1}>
              {selectedManagers.map((manager: any) => (
                <Chip
                  key={manager.value}
                  label={manager.label}
                  size='small'
                  color='primary'
                  icon={<Iconify icon='mdi:account' />}
                />
              ))}
            </Stack>
          </Paper>
        )}

        {/* Quick Add New Seller */}
        <Stack direction='row' justifyContent='center'>
          <Button
            size='small'
            color='primary'
            variant='outlined'
            startIcon={<Iconify icon='mingcute:add-line' />}
            onClick={quickEdit.onTrue}
            sx={{ borderStyle: 'dashed' }}
          >
            Cadastrar novo vendedor
          </Button>
        </Stack>

        {/* Info Box */}
        <Paper
          sx={{ p: 2, bgcolor: 'info.lighter', border: '1px solid', borderColor: 'info.light' }}
        >
          <Stack direction='row' alignItems='center' gap={1}>
            <Iconify icon='mdi:information' color='info.main' />
            <Typography variant='body2' color='info.dark'>
              <strong>Dica:</strong> Os vendedores selecionados poderão acessar este evento,
              visualizar relatórios e realizar vendas.
            </Typography>
          </Stack>
        </Paper>
      </Stack>

      <Stack alignItems='flex-end' justifyContent='space-between' direction='row' sx={{ mt: 3 }}>
        <Button
          disabled={activeStep === 0}
          variant='outlined'
          onClick={handleBack}
          startIcon={<Iconify icon='solar:arrow-left-bold' />}
        >
          Voltar
        </Button>
        <LoadingButton
          loading={isLoading}
          variant='contained'
          onClick={handleNextStep}
          endIcon={<Iconify icon='solar:arrow-right-bold' />}
          sx={{
            minWidth: 200,
            fontWeight: 600,
          }}
        >
          Salvar e Continuar
        </LoadingButton>
      </Stack>

      <UserCreateEditForm
        onRefleshTable={() => {}}
        open={quickEdit.value}
        onClose={quickEdit.onFalse}
      />
    </>
  )
}
